/**
 * usePushNotifications — manages Web Push subscription lifecycle.
 * Handles: permission request, subscription to VAPID endpoint,
 * sync with backend, and unsubscribe.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BACKEND_URL } from "../lib/backend";

const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function usePushNotifications(authHeader) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check initial state
  useEffect(() => {
    const sw = "serviceWorker" in navigator;
    const push = "PushManager" in window;
    const notif = "Notification" in window;
    setSupported(sw && push && notif);
    if (notif) setPermission(Notification.permission);

    // Check if already subscribed in backend
    if (sw && push && authHeader) {
      axios.get(`${API}/pwa/subscription-status`, { headers: authHeader() })
        .then(r => setSubscribed(r.data.subscribed))
        .catch(() => {});
    }
  }, [authHeader]);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return;

      // Get VAPID public key
      const keyRes = await axios.get(`${API}/pwa/vapid-public-key`);
      const vapidKey = keyRes.data.public_key;

      // Get SW registration
      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed in browser
      let pushSub = await registration.pushManager.getSubscription();
      if (!pushSub) {
        pushSub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      // Send subscription to backend
      const subJson = pushSub.toJSON();
      await axios.post(
        `${API}/pwa/subscribe`,
        {
          endpoint: subJson.endpoint,
          keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
        },
        { headers: authHeader() }
      );

      setSubscribed(true);
    } catch (e) {
      console.error("Push subscription failed:", e);
    } finally {
      setLoading(false);
    }
  }, [supported, authHeader]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const pushSub = await registration.pushManager.getSubscription();
      if (pushSub) await pushSub.unsubscribe();
      await axios.delete(`${API}/pwa/subscribe`, { headers: authHeader() });
      setSubscribed(false);
    } catch (e) {
      console.error("Push unsubscribe failed:", e);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
